import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CartItemListing } from '../../components/Cart/CartItemListing';
import { CartTotalsTable } from '../../components/Cart/CartTotalsTable';
import { DisabledFormPart } from '../../components/DisabledFormPart/DisabledFormPart';
import { SimpleEditableCard } from '../../components/EditableCard/SimpleEditableCard';
import { Button } from '../../components/Form/Button';
import { useFormValidationContainer } from '../../components/Form/FormValidatorContainer';
import { Icon, broken_link, close, spinner } from '../../components/Icon/Icon';
import { Modal } from '../../components/Modal/Modal';
import { useConfig } from '../../config';
import { usePrevious } from '../../hooks/usePrevious';
import { useTranslation } from '../../i18n/i18n';
import { Res } from '../../state/api';
import { useCart } from '../../state/cart';
import { CheckoutContainer } from '../../state/checkout';
import { useSession } from '../../state/session';
import {
	ApiCheckoutState,
	ApiCreateOrderError,
	ApiCreateOrderPayload,
	ApiDiscountCode,
	ApiTotals,
	ApiUpdatePaymentPayload,
	PaymentMethodType,
} from '../../types';
import { ContentCard } from '../ContentCard/ContentCard';
import { DeliveryInput, DeliveryModel } from '../Form/DeliveryInput';
import { EmailAddressInput, EmailAddressModel } from '../Form/EmailAddressInput';
import { OrderNotesInput, OrderNotesModel } from '../Form/OrderNotesInput';
import { PaymentInput, PaymentMethodModel, PaymentSummary } from '../Form/PaymentInput';
import './CheckoutCompany.css';
import { CheckoutFooter } from './CheckoutFooter/CheckoutFooter';
import { CheckoutFooterPaypal } from './CheckoutFooter/CheckoutFooterPaypal';
import { DeliveryAddressSection } from './DeliveryAddressSection/DeliveryAddressSection';
import { useStripeInstance } from './Stripe/StripeForm';
import { buildCheckoutState, useCreateOrder } from './utils';

type Props = {
	state: ApiCheckoutState;
	totals?: ApiTotals;
	onChange: (model: ApiCheckoutState) => void;
	homeDeliveryEnabled: boolean;
};

export const CheckoutCompany = ({
	state: checkoutState,
	totals,
	onChange,
	homeDeliveryEnabled,
}: Props): JSX.Element => {
	const emailRef = useRef<HTMLInputElement>(null);
	const t = useTranslation().checkout;
	const r = useTranslation().routes;
	const { api } = useConfig();
	const history = useHistory();
	const { cart, setQuantity } = useCart();
	const { isLoggedIn } = useSession();
	const form = useFormValidationContainer();
	const [hasAppliedCheckoutStateData, setHasAppliedCheckoutStateData] = useState(false);
	const [createOrderStatus, setCreateOrderStatus] = useState<'success' | 'error' | 'loading' | 'cancelled'>();
	const [createOrderErrors, setCreateOrderErrors] = useState<ApiCreateOrderError[]>();
	const { stripe } = useStripeInstance();
	const [deliveryAddressId, setDeliveryAddressId] = useState<number | undefined>(undefined);
	const [deliveryFormOpen, setDeliveryFormOpen] = useState(true);
	const [deliveryValidationTrigger, setDeliveryValidationTrigger] = useState(0);
	const createOrderInFlightRef = useRef(false);
	const paypalRedirectRef = useRef<string>();

	const { createStripePaymentMethod, resetStripePaymentMethod, stripeToken } = CheckoutContainer.useContainer();
	const [paymentFormOpen, setPaymentFormOpen] = useState(
		form.paymentType.value === 'card' ? true : !form.validatePaymentMethod(false)
	);

	const handlePaymentOpen = (open: boolean) => {
		if (form.paymentType.value === 'card') {
			setPaymentFormOpen(true);
			return;
		}

		setPaymentFormOpen(open);
	};

	useEffect(() => {
		// Delivery
		form.deliveryPhone.setValue(checkoutState.delivery?.phone ?? '');
		form.deliveryCompany.setValue(checkoutState.delivery?.company ?? '');
		form.deliveryStreet.setValue(checkoutState.delivery?.street ?? '');
		form.deliveryZip.setValue(checkoutState.delivery?.zip ?? '');
		form.deliveryCity.setValue(checkoutState.delivery?.city ?? '');
		form.deliveryCareOf.setValue(checkoutState.delivery?.careOf ?? '');
		form.deliveryDoorCode.setValue(checkoutState.delivery?.doorCode ?? '');
		form.deliveryFirstname.setValue(checkoutState.delivery?.firstname ?? '');
		form.deliveryLastname.setValue(checkoutState.delivery?.lastname ?? '');
		// Payment
		form.paymentEmail.setValue(checkoutState.payment?.invoiceAddress.email ?? '');
		form.paymentOrgnr.setValue(checkoutState.payment.invoiceAddress.orgnr ?? '');
		form.paymentCity.setValue(checkoutState.payment?.invoiceAddress?.city ?? '');
		form.paymentStreet.setValue(checkoutState.payment?.invoiceAddress?.street ?? '');
		form.paymentCompany.setValue(checkoutState.payment?.invoiceAddress?.company ?? '');
		form.paymentIdentifier.setValue(checkoutState.payment?.einvoice.identifier ?? '');
		form.paymentZip.setValue(checkoutState.payment?.invoiceAddress?.zip ?? '');
		form.paymentRef1.setValue(checkoutState.payment?.invoiceAddress.ref1 ?? '');
		form.paymentOperator.setValue(checkoutState.payment?.einvoice.operator ?? '');
		form.paymentType.setValue(checkoutState.payment?.type ?? '');
		form.paymentCareOf.setValue(checkoutState.payment?.invoiceAddress.careOf ?? '');
		form.comment.setValue(checkoutState.comment ?? '');
		setHasAppliedCheckoutStateData(true);
	}, []);

	useEffect(() => {
		if (checkoutState.payment?.type !== form.paymentType.value) {
			form.paymentType.setValue(checkoutState.payment?.type);
		}
	}, [checkoutState.payment?.type]);

	const previousPaymentType = usePrevious(form.paymentType.value);

	useEffect(() => {
		if (!hasAppliedCheckoutStateData) {
			return;
		}

		if (previousPaymentType === '') {
			setPaymentFormOpen(form.paymentType.value === 'card' ? true : !form.validatePaymentMethod(false));
			return;
		}

		if (previousPaymentType !== undefined && previousPaymentType !== form.paymentType.value) {
			setPaymentFormOpen(true);
			return;
		}

		if (form.paymentType.value === 'card') {
			setPaymentFormOpen(true);
			return;
		}

		const shouldBeOpen = !form.validatePaymentMethod(false, checkoutState.customerType);
		if (shouldBeOpen !== paymentFormOpen) {
			setPaymentFormOpen(shouldBeOpen);
		}
	}, [hasAppliedCheckoutStateData, form.paymentType.value, checkoutState.customerType, previousPaymentType]);
	useEffect(() => {
		if (previousPaymentType !== form.paymentType.value) {
			updateCheckoutStatePayment();
		}
	}, [form.paymentType.value]);

	function updateCheckoutState<T>(model: T) {
		onChange({ ...checkoutState, ...model });
	}

	async function submitEmailAddress(model: EmailAddressModel) {
		// if (!form.paymentEmail.value) {
		form.paymentEmail.setValue(model.email);
		// }
		updateCheckoutState(model);
	}

	async function submitDeliveryMethod(model: DeliveryModel) {
		updateCheckoutState({ delivery: model });
	}

	async function updateCheckoutStatePayment() {
		if (!hasAppliedCheckoutStateData) return;
		const paymentData: ApiCheckoutState['payment'] = {
			type: form.paymentType.value as PaymentMethodType,
			invoiceAddress: {
				email: form.paymentEmail.value,
				orgnr: form.paymentOrgnr.value,
				city: form.paymentCity.value,
				street: form.paymentStreet.value,
				company: form.paymentCompany.value,
				zip: form.paymentZip.value,
				careOf: form.paymentCareOf.value,
				ref1: form.paymentRef1.value,
				ref2: '', // TODO: this is not handled yet in the form
			},
			einvoice: {
				operator: form.paymentOperator.value,
				identifier: form.paymentIdentifier.value,
			},
		};
		updateCheckoutState({ payment: paymentData });
	}

	async function updatePaymentInvoice() {
		const paymentData: ApiCheckoutState['payment'] = {
			type: form.paymentType.value as PaymentMethodType,
			invoiceAddress: {
				email: form.paymentEmail.value,
				orgnr: form.paymentOrgnr.value,
				city: form.paymentCity.value,
				street: form.paymentStreet.value,
				company: form.paymentCompany.value,
				zip: form.paymentZip.value,
				careOf: form.paymentCareOf.value,
				ref1: form.paymentRef1.value,
				ref2: '', // TODO: this is not handled yet in the form
			},
			einvoice: {
				operator: form.paymentOperator.value,
				identifier: form.paymentIdentifier.value,
			},
		};
		axios
			.post<ApiUpdatePaymentPayload>(api + Res.updatePayment.uri({}), paymentData, { withCredentials: true })
			// a background sync of state the ui has already applied; the rejection has to be taken
			.catch(() => undefined);
	}

	async function submitDiscount(model: OrderNotesModel) {
		const { discountCode } = model;

		try {
			await axios.post<ApiDiscountCode>(api + Res.discountCode.uri({}), {
				discountCode,
			});
		} catch (e) {
			// the form fires this and walks away, so the rejection has nowhere else to go; the
			// checkout state is left alone rather than claiming a code that was not applied
			return;
		}

		updateCheckoutState(model);
	}

	async function submitOrderComment(model: OrderNotesModel) {
		updateCheckoutState(model);
	}

	const paymentModel: PaymentMethodModel = {
		type: checkoutState.payment?.type ?? '',
		invoiceEmail: checkoutState.payment?.invoiceAddress.email ?? '',
		einvoiceOperator: checkoutState.payment?.einvoice?.operator ?? '',
		einvoiceIdentifier: checkoutState.payment?.einvoice?.identifier ?? '',
		...checkoutState.payment?.invoiceAddress,
	};

	const createOrder = useCreateOrder();

	async function submit() {
		if (!hasAppliedCheckoutStateData) return;
		if (createOrderStatus === 'loading' || createOrderInFlightRef.current) return;

		createOrderInFlightRef.current = true;
		const paymentType = form.paymentType.value as PaymentMethodType;
		const isCardPayment = paymentType === 'card';
		if (paymentType === 'paypal') {
			paypalRedirectRef.current = undefined;
		}
		const resetCardPaymentAfterError = () => {
			if (!isCardPayment) return;
			resetStripePaymentMethod();
			setPaymentFormOpen(true);
		};

		try {
			const validDeliveryAddress = form.validateDeliveryAddress({ deliveryType: checkoutState.delivery.type });
			const validPaymentMethod =
				paymentType === 'paypal' ? true : form.validatePaymentMethod(true, checkoutState.customerType);
			let stripePaymentMethodId: string | null = null;

			if (isCardPayment && validDeliveryAddress && validPaymentMethod) {
				stripePaymentMethodId = await createStripePaymentMethod();
			}

			const validCardDetails = isCardPayment ? !!stripePaymentMethodId : true;
			form.paymentCard.validate();

			if (!validDeliveryAddress || !validPaymentMethod || !validCardDetails) {
				const el = !validDeliveryAddress
					? document.querySelector('#delivery-form')
					: document.querySelector('#payment-form');
				if (el) {
					setDeliveryFormOpen(true);
					setPaymentFormOpen(true);
					const pos = el.getBoundingClientRect().y + window.pageYOffset - 200;
					window.scrollTo({ top: pos, behavior: 'smooth' });
				}
				if (!validDeliveryAddress) {
					setDeliveryValidationTrigger((prev) => prev + 1);
				}
				return 'INVALID';
			}

			setCreateOrderStatus('loading');

			let payload: ApiCreateOrderPayload = {
				...checkoutState,
				comment: form.comment.value,
				deliveryAddressId,
				delivery: {
					...checkoutState.delivery,
					phone: form.deliveryPhone.value,
					company: form.deliveryCompany.value,
					street: form.deliveryStreet.value,
					zip: form.deliveryZip.value,
					city: form.deliveryCity.value,
					careOf: form.deliveryCareOf.value,
					doorCode: form.deliveryDoorCode.value,
					firstname: form.deliveryFirstname.value,
					lastname: form.deliveryLastname.value,
				},
				payment: {
					...checkoutState.payment,
					type: paymentType,
					invoiceAddress: {
						...checkoutState.payment?.invoiceAddress,
						email: form.paymentEmail.value,
						orgnr: form.paymentOrgnr.value,
						city: form.paymentCity.value,
						street: form.paymentStreet.value,
						company: form.paymentCompany.value,
						zip: form.paymentZip.value,
						careOf: form.paymentCareOf.value,
						ref1: form.paymentRef1.value,
						ref2: '', // TODO: this is not handled yet in the form
					},
					einvoice: {
						...checkoutState.payment?.einvoice,
						operator: form.paymentOperator.value,
						identifier: form.paymentIdentifier.value,
					},
				},
			};
			if (isCardPayment && stripePaymentMethodId) {
				payload.payment.source = {
					type: 'stripe',
					data: stripePaymentMethodId,
				};
			}
			const { data } = await createOrder({ ...payload });

			if (paymentType === 'paypal') {
				if (data.success && data.paypalOrderId) {
					paypalRedirectRef.current = data.redirect || r.companyCheckoutThanks;
					return data.paypalOrderId;
				} else {
					setCreateOrderStatus('error');
					if (data.errors) {
						setCreateOrderErrors(data.errors);
					}
				}
			} else if (data.stripeResult && data.stripeResult.actionRequired) {
				const res = await stripe?.handleCardAction(data.stripeResult.intentClientSecret);
				if (res?.error) {
					resetCardPaymentAfterError();
					setCreateOrderStatus('error');
					return;
				}

				const verifiedCardRes = await axios.post(
					`${api}checkout/verify-card-payment.json`,
					{
						orderId: data.order.id,
					},
					{ withCredentials: true }
				);
				if (verifiedCardRes.data.success) {
					onChange(buildCheckoutState({}));
					history.push(data.redirect || r.companyCheckoutThanks);
				} else {
					resetCardPaymentAfterError();
					setCreateOrderStatus('error');
					return;
				}
			} else {
				if (data.success) {
					onChange(buildCheckoutState({}));
					history.push(data.redirect);
				} else {
					resetCardPaymentAfterError();
					setCreateOrderStatus('error');
					if (data.errors) {
						setCreateOrderErrors(data.errors);
					}
				}
			}
		} catch {
			resetCardPaymentAfterError();
			setCreateOrderStatus('error');
		} finally {
			createOrderInFlightRef.current = false;
		}
	}

	const submitOrderNotes = (value: OrderNotesModel) => {
		if (value.discountCodeChanged) {
			submitDiscount(value);
		}

		if (value.orderCommentChanged) {
			submitOrderComment(value);
		}
	};

	function focusEmailInput() {
		if (emailRef.current) {
			emailRef.current.focus();
		}
	}

	const isValidEmail = !!checkoutState.email;

	return (
		<div>
			<ContentCard title={t.emailAddress}>
				{!isLoggedIn ? (
					<EmailAddressInput
						defaultValue={checkoutState.email}
						ref={emailRef}
						onSubmit={submitEmailAddress}
					/>
				) : (
					checkoutState.email
				)}
			</ContentCard>

			<DisabledFormPart isDisabled={!isValidEmail} onClick={focusEmailInput}>
				<div className="delivery-method">
					<ContentCard title={t.deliveryMethod}>
						<DeliveryInput
							model={checkoutState.delivery}
							homeDeliveryEnabled={homeDeliveryEnabled}
							onChange={submitDeliveryMethod}
							customerType={checkoutState.customerType}
							renderedInCheckout
						/>
					</ContentCard>
				</div>
			</DisabledFormPart>

			<DeliveryAddressSection
				setDeliveryAddressId={setDeliveryAddressId}
				checkoutState={checkoutState}
				updateCheckoutState={updateCheckoutState}
				hasAppliedCheckoutStateData={hasAppliedCheckoutStateData}
				focusEmailInput={focusEmailInput}
				deliveryFormOpen={deliveryFormOpen}
				setDeliveryFormOpen={setDeliveryFormOpen}
				deliveryValidationTrigger={deliveryValidationTrigger}
			/>

			<DisabledFormPart isDisabled={!isValidEmail} onClick={focusEmailInput}>
				<div className="payment-method">
					<SimpleEditableCard
						title={t.paymentMethod}
						onSubmit={() => {
							updateCheckoutStatePayment();

							if (form.paymentType.value === 'card' ? !!stripeToken : form.validatePaymentMethod()) {
								updatePaymentInvoice();
								return true;
							}
							return false;
						}}
						alwaysOpen={!isLoggedIn || form.paymentType.value === 'card'}
						shouldStartOpen={form.paymentType.value === 'card' ? true : !form.validatePaymentMethod(false)}
						renderWhenClosed={() => <PaymentSummary model={paymentModel} />}
						renderWhenOpen={() => (
							<PaymentInput
								onBlur={() => {
									updateCheckoutStatePayment();
								}}
							/>
						)}
						setOpen={handlePaymentOpen}
						open={paymentFormOpen}
						cardId="payment-form"
					/>
				</div>
			</DisabledFormPart>

			<DisabledFormPart isDisabled={!isValidEmail} onClick={focusEmailInput}>
				<ContentCard title={t.orderNotes}>
					<OrderNotesInput
						totals={totals}
						defaultOrderComment={checkoutState.comment}
						defaultDiscountCode={checkoutState.discountCode}
						onChangeComment={(value: string) => form.comment.setValue(value)}
						onBlur={submitOrderNotes}
					/>
				</ContentCard>
			</DisabledFormPart>

			<ContentCard title={t.checkYourCart} className="checkout-cart">
				<div className="not-mobile">
					<cart-item-heading>
						<span>{t.product}</span>
						<span className="text-center">{t.amount}</span>
						<span className="text-right">{t.total}</span>
					</cart-item-heading>
				</div>
				<cart-items>
					<CartItemListing items={cart.items} onSetQuantity={setQuantity} />
				</cart-items>
				<div className="mb-3"></div>
				<CartTotalsTable cart={cart} showLetterFee />
				{checkoutState.payment.type === 'paypal' ? (
					<CheckoutFooterPaypal
						disabled={cart.items.length === 0}
						submit={submit}
						successCallback={() => {
							onChange(buildCheckoutState({}));
							history.push(paypalRedirectRef.current || r.companyCheckoutThanks);
						}}
						errorCallback={(error: ApiCreateOrderError) => {
							if (error.message === 'Window is closed, can not determine type') {
								setCreateOrderStatus('cancelled');
							} else if (error.message === 'Form is invalid') {
								return;
							} else {
								setCreateOrderStatus('error');
								if (error) {
									setCreateOrderErrors([error]);
								}
							}
						}}
						setCreateOrderStatus={setCreateOrderStatus}
					/>
				) : (
					<CheckoutFooter
						disabled={cart.items.length === 0 || createOrderStatus === 'loading'}
						submit={submit}
					/>
				)}
			</ContentCard>

			<Modal className="create-order-modal" show={!!createOrderStatus} onDismiss={() => ({})}>
				<ContentCard className="create-order-content">
					<button className="close-btn" onClick={() => setCreateOrderStatus(undefined)}>
						<Icon shape={{ ...close, fill: 'black' }} size={20} />
					</button>

					<div className="create-order-content-main">
						{createOrderStatus === 'cancelled' && (
							<>
								<span className="create-order-icon">
									<Icon shape={broken_link} size={44} />
								</span>
								<p className="create-order-title">{t.modal.cancelTitle}</p>
								<p className="create-order-text">{t.modal.cancelMessage}</p>
							</>
						)}

						{createOrderStatus === 'loading' && (
							<>
								<span className="create-order-icon">
									<Icon shape={spinner} size={44} />
								</span>
								<p className="create-order-title">{t.modal.statusTitle}</p>
								<p className="create-order-text">{t.modal.statusMessage}</p>
							</>
						)}

						{createOrderStatus === 'error' && (
							<>
								<Icon shape={broken_link} size={44} />
								<p className="create-order-title">{t.modal.errorTitle}</p>
								<p className="create-order-text">{t.modal.errorMessage}</p>
								{createOrderErrors &&
									createOrderErrors.map((error) => (
										<code className="error">
											{error.code} - {error.message}
										</code>
									))}
								<div>
									<Button onClick={() => setCreateOrderStatus(undefined)}>{t.modal.ok}</Button>
								</div>
							</>
						)}
					</div>
				</ContentCard>
			</Modal>
		</div>
	);
};
